import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useStores } from "@/context/StoreContext";
import { PageHeader, Loading, EmptyState, StatusBadge, StatCard } from "@/components/common";
import { numberID, formatDateTime } from "@/lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Undo2, PackageCheck, PackageX } from "lucide-react";

export default function Returns() {
  const { activeStore } = useStores();
  const [rows, setRows] = useState(null);
  const [nonSellable, setNonSellable] = useState([]);

  useEffect(() => {
    setRows(null);
    const params = activeStore !== "all" ? { store_id: activeStore } : {};
    api.get("/returns", { params }).then((r) => setRows(r.data));
    api.get("/returns/non-sellable").then((r) => setNonSellable(r.data));
  }, [activeStore]);

  const sellable = (rows || []).reduce((s, r) => s + r.total_sellable, 0);
  const damaged = (rows || []).reduce((s, r) => s + r.total_damaged, 0);

  return (
    <div>
      <PageHeader title="Retur" subtitle="Barang layak jual otomatis kembali ke Inventory Pusat; barang rusak masuk Stok Non-Jual dan tidak menambah Available." testId="returns-header" />
      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <StatCard label="Total Retur" value={numberID((rows || []).length)} icon={Undo2} accent="indigo" testId="returns-total" />
        <StatCard label="Kembali ke Stok" value={numberID(sellable)} icon={PackageCheck} accent="emerald" hint="pcs layak jual" testId="returns-sellable" />
        <StatCard label="Stok Non-Jual" value={numberID(damaged)} icon={PackageX} accent="rose" hint="pcs rusak" testId="returns-damaged" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        {!rows ? <Loading /> : rows.length === 0 ? <EmptyState title="Belum ada retur" subtitle="Proses retur dari halaman Pesanan (status Dikirim/Selesai)." icon={Undo2} /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>No. Retur</TableHead><TableHead>Waktu</TableHead><TableHead>Pesanan</TableHead><TableHead>Toko</TableHead><TableHead>Item</TableHead><TableHead className="text-right">Layak Jual</TableHead><TableHead className="text-right">Rusak</TableHead><TableHead>Status Retur</TableHead><TableHead>Oleh</TableHead></TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`return-row-${r.return_no}`}>
                    <TableCell className="font-mono text-xs font-semibold">{r.return_no}</TableCell>
                    <TableCell className="text-xs text-slate-500">{formatDateTime(r.created_at)}</TableCell>
                    <TableCell className="font-mono text-xs">{r.order_no}</TableCell>
                    <TableCell>{r.store_name}</TableCell>
                    <TableCell className="text-xs text-slate-500">{r.items.map((it) => `${it.sku} ×${it.qty}`).join(", ")}</TableCell>
                    <TableCell className="text-right font-semibold text-emerald-600">{numberID(r.total_sellable)}</TableCell>
                    <TableCell className="text-right font-semibold text-rose-600">{numberID(r.total_damaged)}</TableCell>
                    <TableCell><StatusBadge status={r.order_return_status} /></TableCell>
                    <TableCell className="text-xs text-slate-500">{r.created_by_name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {nonSellable.length > 0 && (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="non-sellable-table">
          <div className="border-b border-slate-100 px-4 py-3"><p className="font-display text-sm font-semibold text-slate-800">Stok Non-Jual per SKU</p></div>
          <Table>
            <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead className="text-right">Qty Non-Jual</TableHead></TableRow></TableHeader>
            <TableBody>
              {nonSellable.map((n) => (
                <TableRow key={n.ref_id} data-testid={`non-sellable-${n.sku}`}><TableCell className="font-mono text-xs font-semibold text-indigo-700">{n.sku}</TableCell><TableCell className="text-right font-bold text-rose-600">{numberID(n.non_sellable)}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
